/**
 * Network Service
 * 
 * Servicio para monitorear el estado de la conexión de red.
 * REQUERIDO para modo offline y experiencia nativa.
 * 
 * @module native/network
 */

import { Network, ConnectionStatus, ConnectionType } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('NetworkService');

export interface NetworkStatus {
  connected: boolean;
  connectionType: ConnectionType;
}

type NetworkChangeCallback = (status: NetworkStatus) => void;

class NetworkService {
  private static instance: NetworkService;
  private listeners: Set<NetworkChangeCallback> = new Set();
  private currentStatus: NetworkStatus = {
    connected: true,
    connectionType: 'unknown',
  };
  private isInitialized = false;

  private constructor() {}

  static getInstance(): NetworkService {
    if (!NetworkService.instance) {
      NetworkService.instance = new NetworkService();
    }
    return NetworkService.instance;
  }

  /**
   * Verifica si estamos en un dispositivo nativo
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Inicializa el monitoreo de red
   */
  async initialize(): Promise<NetworkStatus> {
    if (this.isInitialized) {
      return this.currentStatus;
    }

    try {
      // Obtener estado actual
      const status = await Network.getStatus();
      this.currentStatus = {
        connected: status.connected,
        connectionType: status.connectionType,
      };

      // Configurar listener para cambios
      Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        const newStatus: NetworkStatus = {
          connected: status.connected,
          connectionType: status.connectionType,
        };

        const wasOffline = !this.currentStatus.connected;
        const isNowOnline = status.connected;

        this.currentStatus = newStatus;

        logger.info('Cambio en estado de red', {
          connected: status.connected,
          type: status.connectionType,
        });

        // Notificar a todos los listeners
        this.notifyListeners(newStatus);

        // Si recuperamos conexión, disparar evento especial
        if (wasOffline && isNowOnline) {
          this.onConnectionRestored();
        }
      });

      this.isInitialized = true;
      logger.info('NetworkService inicializado', this.currentStatus);

      return this.currentStatus;
    } catch (error) {
      logger.error('Error inicializando NetworkService', { error });
      // En caso de error, asumir que hay conexión
      return { connected: true, connectionType: 'unknown' };
    }
  }

  /**
   * Obtiene el estado actual de la red
   */
  async getStatus(): Promise<NetworkStatus> {
    try {
      const status = await Network.getStatus();
      this.currentStatus = {
        connected: status.connected,
        connectionType: status.connectionType,
      };
      return this.currentStatus;
    } catch (error) {
      logger.error('Error obteniendo estado de red', { error });
      return this.currentStatus;
    }
  }

  /**
   * Verifica si hay conexión a internet
   */
  isOnline(): boolean {
    return this.currentStatus.connected;
  }

  /**
   * Verifica si estamos offline
   */
  isOffline(): boolean {
    return !this.currentStatus.connected;
  }

  /**
   * Obtiene el tipo de conexión
   */
  getConnectionType(): ConnectionType {
    return this.currentStatus.connectionType;
  }

  /**
   * Verifica si estamos en WiFi
   */
  isOnWifi(): boolean {
    return this.currentStatus.connectionType === 'wifi';
  }

  /**
   * Verifica si estamos en datos móviles
   */
  isOnCellular(): boolean {
    return this.currentStatus.connectionType === 'cellular';
  }

  /**
   * Registra un listener para cambios de red
   */
  addListener(callback: NetworkChangeCallback): () => void {
    this.listeners.add(callback);
    
    // Retorna función para desregistrar
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Elimina un listener
   */
  removeListener(callback: NetworkChangeCallback): void {
    this.listeners.delete(callback);
  }

  /**
   * Notifica a todos los listeners
   */
  private notifyListeners(status: NetworkStatus): void {
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        logger.error('Error en listener de red', { error });
      }
    });
  }

  /**
   * Callback cuando se restaura la conexión
   * Aquí se puede sincronizar datos offline
   */
  private onConnectionRestored(): void {
    logger.info('Conexión restaurada - iniciando sincronización');
    // Disparar evento personalizado para sincronización
    window.dispatchEvent(new CustomEvent('connection-restored'));
  }

  /**
   * Ejecuta una acción solo si hay conexión
   */
  async executeIfOnline<T>(action: () => Promise<T>, offlineMessage?: string): Promise<T | null> {
    if (this.isOffline()) {
      logger.warn(offlineMessage || 'Acción cancelada - sin conexión');
      return null;
    }
    return action();
  }

  /**
   * Espera a que haya conexión (con timeout)
   */
  async waitForConnection(timeoutMs: number = 30000): Promise<boolean> {
    if (this.isOnline()) {
      return true;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.removeListener(checkConnection);
        resolve(false);
      }, timeoutMs);

      const checkConnection = (status: NetworkStatus) => {
        if (status.connected) {
          clearTimeout(timeout);
          this.removeListener(checkConnection);
          resolve(true);
        }
      };

      this.addListener(checkConnection);
    });
  }

  /**
   * Limpieza al destruir
   */
  async cleanup(): Promise<void> {
    try {
      await Network.removeAllListeners();
      this.listeners.clear();
      this.isInitialized = false;
      logger.info('NetworkService cleanup completado');
    } catch (error) {
      logger.error('Error en cleanup de NetworkService', { error });
    }
  }
}

export const networkService = NetworkService.getInstance();










